import { replaceFileUnsafe } from '__core/shared/util/transform';
import Document from '__core/shared/model/document/Document';
import { DocumentType } from '__core/shared/model/document/Document.enum';
import stDocumentAdd from './documentAdd.component';
import documentComplianceController from './documentComplianceController.controller';
import documentEditPdfController from './documentEditPdf.controller';
import stDocumentMergeFields from './documentMergeFields.component';
import stDocumentRename from './documentRename.component';
import documentsController from './documents.controller';
import stDocumentsList from './documentsList.component';

export default angular
  .module('st.Document', ['st.Core', 'angular-cache', 'ngFileUpload'])
  .factory('Document', () => Document)
  .factory('DocumentType', () => DocumentType)
  .component('stDocumentAdd', stDocumentAdd)
  .controller('documentComplianceController', documentComplianceController)
  .controller('documentEditPdfController', documentEditPdfController)
  .component('stDocumentMergeFields', stDocumentMergeFields)
  .component('stDocumentRename', stDocumentRename)
  .controller('documentsController', documentsController)
  .component('stDocumentsList', stDocumentsList)
  .service('documentService', ($http, Document, dataService, DOCUMENT) => ({
    createDocument: (doc) => dataService.createDocument(Document, doc),
    readDocument: (doc) => dataService.readDocument(Document, doc),
    updateDocument: (doc) => dataService.updateDocument(Document, doc),
    deleteDocument: (doc) => dataService.deleteDocument(Document, doc),
    mergeDocument: (params) =>
      dataService.apiRequest(
        null,
        null,
        true,
        null,
        $http.post,
        DOCUMENT.API.MERGE,
        { params },
        ['idWorkflow', 'idStage', 'idTicket', 'idDocuments'],
        true
      ),
    mergeSpecificDocument: (params) =>
      dataService.apiRequest(
        null,
        null,
        true,
        null,
        $http.post,
        DOCUMENT.API.MERGE_SPECIFIC,
        { params },
        ['idTicket', 'documents'],
        true
      ),
    previewPdf: (params) =>
      dataService
        .apiRequest(
          null,
          null,
          true,
          null,
          $http.post,
          DOCUMENT.API.DOWNLOAD_PDF,
          {
            params,
            __requestConfiguration: {
              responseType: 'arraybuffer',
              transformResponse: (response) => ({
                response: response
                  ? new Blob([response], { type: 'application/octet-stream' })
                  : null
              })
            }
          },
          ['idDocument', 'name'],
          true
        )
        .then((data) => {
          saveAs(
            data.response,
            `${replaceFileUnsafe(params.name.replace('.pdf', ''))}_preview.pdf`
          );
        })
  }))
  .service(
    'documentsService',
    ($http, $rootScope, CacheFactory, dataService, Document, DOCUMENT) => {
      let cache;
      if (!CacheFactory.get(DOCUMENT.CACHE.NAME)) {
        cache = CacheFactory(DOCUMENT.CACHE.NAME, DOCUMENT.CACHE.STORE);
      }
      const cacheKey = (params, requestName) =>
        JSON.stringify({
          idAccount: $rootScope.CurrentAccount.id,
          idOrganization: $rootScope.CurrentOrganization.id,
          type: requestName || DOCUMENT.CACHE.NAME,
          params
        });
      return {
        clearCache: () => cache.removeAll(),
        getAvailable: (params) =>
          dataService.apiRequest(
            cache,
            cacheKey(params),
            params?.ignoreCache === true,
            Document.build,
            $http.get,
            DOCUMENT.API.AVAILABLE,
            { params }
          ),
        combinePdf: (params) =>
          dataService.apiRequest(null, null, null, null, $http.post, DOCUMENT.API.COMBINE_PDF, {
            params
          })
      };
    }
  )
  .constant('DOCUMENT', {
    API: {
      AVAILABLE: '/api/v1/documents/available/',
      COMBINE_PDF: '/api/v1/documents/combine-pdf/',
      UPLOAD: '/api/v1/document/upload/',
      DOWNLOAD: '/api/v1/document/download/',
      DOWNLOAD_PDF: '/api/v1/document/download/pdf/',
      MERGE: '/api/v1/document/merge/',
      MERGE_SPECIFIC: '/api/v1/document/merge-specific/'
    },
    DOWNLOAD_DOCUMENT_AS_PDF: ['docx', 'doc', 'xls', 'xlsx'],
    TYPES: [
      {
        id: 1,
        value: 'Credit guide',
        weight: 1,
        isRequired: true,
        isRequiredForCompliance: false
      },
      {
        id: 2,
        value: 'Privacy disclosure',
        weight: 2,
        isRequired: true,
        isRequiredForCompliance: false
      },
      {
        id: 3,
        value: 'Fact find',
        weight: 3,
        isRequired: false,
        isRequiredForCompliance: false
      },
      {
        id: 4,
        value: 'Preliminary assessment',
        weight: 4,
        isRequired: true,
        isRequiredForCompliance: true
      },
      {
        id: 5,
        value: 'Credit proposal disclosure',
        weight: 5,
        isRequired: true,
        isRequiredForCompliance: true
      },
      {
        id: 6,
        value: 'Credit quote',
        weight: 6,
        isRequired: false,
        isRequiredForCompliance: false
      },
      {
        id: 7,
        value: 'Sales document',
        weight: 7,
        isRequired: false,
        isRequiredForCompliance: false
      }
    ],
    CLIENT_TYPES: [
      { id: 'CLIENTS', value: 'Exclude security guarantor(s)', weight: 1 },
      { id: 'SECURITY_GUARANTORS', value: 'Only security guarantor(s)', weight: 2 },
      { id: 'ALL', value: 'All contact(s)', weight: 3 }
    ],
    LIMITS: {
      MULTIPLE: true,
      ALLOWED_MIME:
        '.docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-word,' +
        '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,' +
        '.pptx,.ppt,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,' +
        '.pdf,application/pdf,' +
        '.jpeg,.jpg,image/jpeg,' +
        '.png,image/png',
      ALLOWED_EXTENSIONS: '.docx,.doc,.xlsx,.xls,.pptx,.ppt,.pdf,.jpeg,.jpg,.png'
    },
    CACHE: {
      NAME: 'documentsCache',
      STORE: {
        maxAge: 60 * 1000,
        deleteOnExpire: 'aggressive',
        recycleFreq: 30 * 1000,
        storageMode: 'sessionStorage'
      }
    }
  });
